import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { getPaidMembersTierReport, getPaidMembersList, getRegisteredMembers } from "../../services/api";

const errMsg = (e) => e?.message || "Something went wrong. Please try again.";
const unwrap = (res) => {
    if (res?.status?.success === false) throw res;
    return res?.data;
};

export const fetchPaidTierReport = createAsyncThunk("membersReport/paidTier", async (filters, { rejectWithValue }) => {
    try {
        return unwrap(await getPaidMembersTierReport(filters));
    } catch (e) {
        return rejectWithValue(errMsg(e));
    }
});

export const fetchPaidMembers = createAsyncThunk("membersReport/paidMembers", async ({ perPage, page, filters }, { rejectWithValue }) => {
    try {
        return unwrap(await getPaidMembersList(perPage, page, filters));
    } catch (e) {
        return rejectWithValue(errMsg(e));
    }
});

export const fetchRegisteredMembers = createAsyncThunk("membersReport/registered", async ({ perPage, page, filters }, { rejectWithValue }) => {
    try {
        return unwrap(await getRegisteredMembers(perPage, page, filters));
    } catch (e) {
        return rejectWithValue(errMsg(e));
    }
});

const list = () => ({ rows: [], total: 0, lastPage: 1, loading: false, error: null, reqId: null });

const initialState = {
    tier: { data: null, loading: false, error: null, reqId: null },
    drill: list(),
    registered: { ...list(), summary: null },
};

const membersReportSlice = createSlice({
    name: "membersReport",
    initialState,
    reducers: {
        clearDrill: (state) => {
            state.drill = list();
        },
    },
    extraReducers: (b) => {
        // Every request remembers its id; late responses from an older filter set are ignored.
        b.addCase(fetchPaidTierReport.pending, (s, a) => {
            s.tier.loading = true; s.tier.error = null; s.tier.reqId = a.meta.requestId;
        })
            .addCase(fetchPaidTierReport.fulfilled, (s, a) => {
                if (s.tier.reqId !== a.meta.requestId) return;
                s.tier.loading = false; s.tier.data = a.payload;
            })
            .addCase(fetchPaidTierReport.rejected, (s, a) => {
                if (s.tier.reqId !== a.meta.requestId) return;
                s.tier.loading = false; s.tier.error = a.payload || errMsg(a.error);
            });

        b.addCase(fetchPaidMembers.pending, (s, a) => {
            s.drill.loading = true; s.drill.error = null; s.drill.reqId = a.meta.requestId;
        })
            .addCase(fetchPaidMembers.fulfilled, (s, a) => {
                if (s.drill.reqId !== a.meta.requestId) return;
                s.drill.loading = false;
                s.drill.rows = a.payload?.data || [];
                s.drill.total = a.payload?.total || 0;
                s.drill.lastPage = a.payload?.last_page || 1;
            })
            .addCase(fetchPaidMembers.rejected, (s, a) => {
                if (s.drill.reqId !== a.meta.requestId) return;
                s.drill.loading = false; s.drill.error = a.payload || errMsg(a.error);
            });

        b.addCase(fetchRegisteredMembers.pending, (s, a) => {
            s.registered.loading = true; s.registered.error = null; s.registered.reqId = a.meta.requestId;
        })
            .addCase(fetchRegisteredMembers.fulfilled, (s, a) => {
                if (s.registered.reqId !== a.meta.requestId) return;
                s.registered.loading = false;
                s.registered.summary = a.payload?.summary || null;
                s.registered.rows = a.payload?.members?.data || [];
                s.registered.total = a.payload?.members?.total || 0;
                s.registered.lastPage = a.payload?.members?.last_page || 1;
            })
            .addCase(fetchRegisteredMembers.rejected, (s, a) => {
                if (s.registered.reqId !== a.meta.requestId) return;
                s.registered.loading = false; s.registered.error = a.payload || errMsg(a.error);
            });
    },
});

export const { clearDrill } = membersReportSlice.actions;
export default membersReportSlice.reducer;